/**
 * navGuardService — bridges BrowserRouter's getUserConfirmation callback to React state.
 *
 * Standalone mode flow:
 *   1. BrowserRouter getUserConfirmation fires request(msg, cb).
 *   2. NavigationGuard (mounted in App) subscribes, sees isPending()===true, renders the modal.
 *   3. Modal "Stay" → resolve(false); "Leave anyway" → resolve(true).
 *
 * Iframe (Punchup) mode flow:
 *   1. BrowserRouter getUserConfirmation fires request(msg, cb).
 *   2. navGuardService posts { action: 'navigation-guard', message } to the parent frame.
 *   3. Parent (downstroke ShowComponent) shows a confirmation modal.
 *   4. User responds → parent posts { action: 'navigation-guard-response', confirmed } back.
 *   5. The module-level listener below calls resolve(confirmed).
 *
 * Module-level singleton: safe because only one navigation attempt can be in flight at a time.
 */

type ResolveCallback = (ok: boolean) => void;

// If the parent frame never posts navigation-guard-response (e.g. the AlertDialog
// crashes or is never rendered), the BrowserRouter callback hangs forever.
// Auto-resolve with confirmed=true after this delay so the iframe can navigate.
const GUARD_TIMEOUT_MS = 30_000;

let _callback: ResolveCallback | null = null;
let _timeoutId: ReturnType<typeof setTimeout> | null = null;
const _listeners = new Set<() => void>();

const notify = () => _listeners.forEach((fn) => fn());

export const navGuardService = {
	/**
	 * Called by BrowserRouter's getUserConfirmation.
	 * In iframe mode, delegates to parent frame and skips local modal.
	 * In standalone mode, notifies local subscribers (NavigationGuard renders).
	 */
	request(_message: string, callback: ResolveCallback): void {
		// Clear any zombie timeout left by a previous request() that was never resolved.
		// Without this, a stale timer would fire at its original deadline and incorrectly
		// auto-resolve the new request's callback rather than the old one.
		if (_timeoutId !== null) {
			clearTimeout(_timeoutId);
			_timeoutId = null;
		}
		_callback = callback;
		if (window.parent !== window) {
			// Start the safety-net timer before posting so the iframe can never hang
			// permanently if the parent frame fails to respond.
			_timeoutId = setTimeout(() => {
				_timeoutId = null;
				if (_callback) navGuardService.resolve(true);
			}, GUARD_TIMEOUT_MS);
			window.parent.postMessage(
				{ action: 'navigation-guard', message: _message },
				process.env.REACT_APP_DOWNSTROKE_PORTAL_URL || '*'
			);
		} else {
			notify();
		}
	},

	/** Called by NavigationGuard's confirm/cancel buttons (standalone) or the response listener (iframe). */
	resolve(ok: boolean): void {
		if (_timeoutId !== null) {
			clearTimeout(_timeoutId);
			_timeoutId = null;
		}
		_callback?.(ok);
		_callback = null;
		notify();
	},

	/** True when a navigation attempt is waiting for the user's decision. */
	isPending(): boolean {
		return _callback !== null;
	},

	/** Subscribe to state changes. Returns an unsubscribe function for useEffect cleanup. */
	subscribe(fn: () => void): () => void {
		_listeners.add(fn);
		return () => _listeners.delete(fn);
	},
};

// Listen for navigation-guard-response from the parent frame (iframe mode).
// Resolves the pending callback so react-router can proceed or stay.
if (typeof window !== 'undefined') {
	window.addEventListener('message', (event: MessageEvent) => {
		if (event.data?.action === 'navigation-guard-response' && _callback) {
			navGuardService.resolve(event.data.confirmed === true);
		}
	});
}
